import { t } from "@lingui/macro";
import isEqual from "lodash/isEqual";
import { useEffect, useMemo, useState } from "react";
import { ScrollView } from "react-native";
import { PreloadedQuery } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { InfoBanner } from "@whoosh/design-system/src/components/display/InfoBanner";
import { AlertIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import {
  Box,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { NotificationRow } from "./NotificationRow";
import { useNotificationsUpdateModalDataQuery } from "./hooks/__generated__/useNotificationsUpdateModalDataQuery.graphql";
import { NotificationChannel } from "./hooks/__generated__/useSetUserNotificationPreferencesMutation.graphql";
import {
  NotificationPreferencesChannels,
  useNotificationsUpdateModalData,
} from "./hooks/useNotificationsUpdateModalData";
import { useSetUserNotificationPreferences } from "./hooks/useSetUserNotificationPreferences";

type NotificationCategoryKey = keyof NotificationPreferencesChannels;

type VerificationTarget = "email" | "phone" | null;

type NotificationsUpdateModalInnerProps = {
  onClose: () => void;
  queryRef: PreloadedQuery<useNotificationsUpdateModalDataQuery>;
  onRequestVerification?: (mode: "email" | "phone") => void;
  showGuide?: boolean;
  onSaveSuccess?: () => void;
};

const defaultPreferences: NotificationPreferencesChannels = {
  bookingUpdates: [],
  bookingReminders: [],
  requestedTimeAvailable: [],
  eventUpdates: [],
  eventReminders: [],
};

export const NotificationsUpdateModalInner = ({
  onClose,
  queryRef,
  onRequestVerification,
  showGuide,
  onSaveSuccess,
}: NotificationsUpdateModalInnerProps) => {
  const {
    contactEmail,
    contactPhone,
    isEmailVerified,
    isPhoneVerified,
    userId,
    preferences: fetchedPreferences,
  } = useNotificationsUpdateModalData(queryRef);

  const emailMissing = !contactEmail;
  const emailUnverified = !!contactEmail && !isEmailVerified;
  const emailDisabled = emailMissing || emailUnverified;

  const phoneMissing = !contactPhone;
  const phoneUnverified = !!contactPhone && !isPhoneVerified;
  const smsDisabled = phoneMissing || phoneUnverified;

  const stripDisabledChannels = useMemo(() => {
    const removeDisallowed = (
      prefs: NotificationPreferencesChannels
    ): NotificationPreferencesChannels => ({
      bookingUpdates: prefs.bookingUpdates.filter(
        (channel) =>
          (channel !== "EMAIL" || !emailDisabled) &&
          (channel !== "SMS" || !smsDisabled)
      ),
      bookingReminders: prefs.bookingReminders.filter(
        (channel) =>
          (channel !== "EMAIL" || !emailDisabled) &&
          (channel !== "SMS" || !smsDisabled)
      ),
      requestedTimeAvailable: prefs.requestedTimeAvailable.filter(
        (channel) =>
          (channel !== "EMAIL" || !emailDisabled) &&
          (channel !== "SMS" || !smsDisabled)
      ),
      eventUpdates: prefs.eventUpdates.filter(
        (channel) =>
          (channel !== "EMAIL" || !emailDisabled) &&
          (channel !== "SMS" || !smsDisabled)
      ),
      eventReminders: prefs.eventReminders.filter(
        (channel) =>
          (channel !== "EMAIL" || !emailDisabled) &&
          (channel !== "SMS" || !smsDisabled)
      ),
    });

    return removeDisallowed;
  }, [emailDisabled, smsDisabled]);

  const initialPreferences = useMemo(() => {
    const base = fetchedPreferences ?? defaultPreferences;
    return stripDisabledChannels(base);
  }, [fetchedPreferences, stripDisabledChannels]);

  const [preferences, setPreferences] =
    useState<NotificationPreferencesChannels>(initialPreferences);

  useEffect(() => {
    setPreferences(initialPreferences);
  }, [initialPreferences]);

  const { showToast } = useToast();

  const onSuccess = () => {
    onSaveSuccess?.();
    showToast({
      description: t`Your notification preferences have been updated successfully.`,
      semanticStyle: "success",
      toastId: "user-notification-preferences-update",
    });
    onClose();
  };

  const onError = () => {
    showToast({
      description: t`Woops! There was an error saving your preferences.`,
      semanticStyle: "error",
      toastId: "user-notification-preferences-error",
    });
  };

  const [updateUserNotificationPreferences, isUpdating] =
    useSetUserNotificationPreferences(onSuccess, onError);

  const hasChanges = useMemo(
    () => !isEqual(preferences, initialPreferences),
    [preferences, initialPreferences]
  );

  const handleChannelChange = (
    key: NotificationCategoryKey,
    values: NotificationChannel[]
  ) => {
    const sanitized = Array.from(new Set(values)).filter(
      (channel) =>
        (channel !== "EMAIL" || !emailDisabled) &&
        (channel !== "SMS" || !smsDisabled)
    );
    setPreferences((prev) => ({ ...prev, [key]: sanitized }));
  };

  const handleSave = () => {
    if (!userId) return;
    updateUserNotificationPreferences({
      userId,
      preferences,
    });
  };

  const verificationTarget: VerificationTarget = useMemo(() => {
    const needsEmailVerification = emailUnverified;
    const needsPhoneVerification = phoneUnverified;

    if (needsEmailVerification) return "email";
    if (needsPhoneVerification) return "phone";
    return null;
  }, [emailUnverified, phoneUnverified]);

  const channelOptions = useMemo<
    React.ComponentProps<typeof NotificationRow>["options"]
  >(
    () => [
      {
        label: contactEmail ? `${t`Email`} ${contactEmail}` : t`Email`,
        value: "EMAIL",
        isDisabled: emailDisabled,
      },
      {
        label: contactPhone ? `${t`SMS`} ${contactPhone}` : t`SMS`,
        value: "SMS",
        isDisabled: smsDisabled,
      },
      {
        label: t`Push Notification`,
        value: "PUSH",
        showUnderline: true,
      },
      {
        label: t`Turn Off`,
        value: "OFF",
      },
    ],
    [contactEmail, contactPhone, emailDisabled, smsDisabled]
  );

  const shouldShowFooter = hasChanges && !!userId;

  return (
    <Box flex={1}>
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ flexGrow: 1 }}>
        <VStack px={6} pt={6} pb={2}>
          {verificationTarget ? (
            <InfoBanner
              colorTheme="NEUTRAL"
              leadingIcon={AlertIcon}
              message={
                verificationTarget === "email"
                  ? t`Verify your email address to use Email notifications.`
                  : t`Verify your phone number to use SMS notifications.`
              }
              action={
                onRequestVerification
                  ? {
                      label: t`Verify now`,
                      handler: () => {
                        onRequestVerification(verificationTarget);
                      },
                    }
                  : undefined
              }
            />
          ) : null}

          <Text
            variant="mainTiny"
            color="text-secondary"
            mt={verificationTarget ? 5 : 3}
            mb={3}
          >
            {t`MY BOOKINGS`}
          </Text>
          <NotificationRow
            label={t`Booking Updates`}
            values={preferences.bookingUpdates}
            options={channelOptions}
            testID="booking-updates-notification-row"
            onChange={(values) => handleChannelChange("bookingUpdates", values)}
          />
          <NotificationRow
            label={t`Booking Reminders`}
            values={preferences.bookingReminders}
            options={channelOptions}
            testID="booking-reminders-notification-row"
            onChange={(values) =>
              handleChannelChange("bookingReminders", values)
            }
          />
          <NotificationRow
            label={t`Requested time becomes available`}
            values={preferences.requestedTimeAvailable}
            options={channelOptions}
            onChange={(values) =>
              handleChannelChange("requestedTimeAvailable", values)
            }
            showGuide={showGuide}
            testID="requested-time-becomes-available-notification-row"
          />

          <Text variant="mainTiny" color="text-secondary" mt={6} mb={3}>
            {t`MY EVENTS`}
          </Text>
          <NotificationRow
            label={t`Event Joined`}
            values={preferences.eventReminders}
            options={channelOptions}
            testID="event-joined-notification-row"
            onChange={(values) => handleChannelChange("eventReminders", values)}
          />
          <NotificationRow
            label={t`Event Updates`}
            testID="event-updates-notification-row"
            values={preferences.eventUpdates}
            options={channelOptions}
            onChange={(values) => handleChannelChange("eventUpdates", values)}
          />
        </VStack>
      </ScrollView>
      {shouldShowFooter ? (
        <Box
          borderTopColor="surface-dividers"
          borderTopWidth={1}
          paddingHorizontal={6}
          paddingVertical={5}
          backgroundColor="surface-primary"
        >
          <Button
            onPress={handleSave}
            isLoading={isUpdating}
            width="100%"
            size="large"
            accessibilityLabel={t`Save Changes and Close Button`}
            testID="save-changes-and-close-button"
          >
            {t`Save Changes and Close`}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};
